import React from 'react';
import { useTranslation } from 'react-i18next';
import { DayHours, DayType } from '../../types';
import { getDayTypeLabel } from '../../utils/dayTypeDetector';
import { pickLang } from '../../utils/listingDisplay';

/** Special days worth naming next to the weekday, most specific first. */
const NAMED_DAY_TYPES: DayType[] = [
  'yomKippur',
  'roshHashana',
  'pesachI',
  'shviiShelPesach',
  'shavuot',
  'sukkotI',
  'simchatTorah',
  'yomTov',
  'tishaBav',
  'purim',
  'fastMinor',
  'hoshanaRabba',
  'cholHaMoed',
  'erevYomTov',
  'roshChodesh',
];

/** The day's most specific special-day name ("Pesach"), if any. Shiur weeks use it too. */
export function specialDayLabel(day: Pick<DayHours, 'dayTypes'>, lang: 'en' | 'he'): string | undefined {
  const type = NAMED_DAY_TYPES.find((d) => day.dayTypes.includes(d));
  return type ? getDayTypeLabel(type, lang) : undefined;
}

/** Times stay left-to-right inside Hebrew text. */
export function Time({ children }: { children: React.ReactNode }) {
  return <span dir="ltr" className="tabular-nums">{children}</span>;
}

export default function WeekHours({ week }: { week: DayHours[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const lang = isHebrew ? 'he' : 'en';
  const anyMayDiffer = week.some((d) => d.mayDiffer);

  return (
    <div className="space-y-2">
      <ul className="divide-y divide-ink/5">
        {week.map((day, i) => {
          const special = specialDayLabel(day, lang);
          return (
            <li key={day.date} className={`flex items-start justify-between gap-3 py-2 text-sm ${i === 0 ? 'font-semibold' : ''}`}>
              <span className="text-ink">
                {i === 0 ? t('directory.detail.today') : t(`directory.dayOfWeek.${day.dayOfWeek}`)}
                {special && <span className="block text-xs text-ash font-normal">{special}</span>}
              </span>
              <span className="text-end text-ink">
                {day.callToCheck ? (
                  <span className="text-ash">{t('directory.openStatus.callToCheck')}</span>
                ) : day.closed || day.ranges.length === 0 ? (
                  <span className="text-ash">{t('directory.detail.closedDay')}</span>
                ) : (
                  day.ranges.map((r) => {
                    const note = pickLang(r.noteEn, r.noteHe, isHebrew);
                    return (
                      <span key={r.entryId} className="block">
                        <Time>
                          {r.open}–{r.close}
                        </Time>
                        {r.closesNextDay && <span className="text-xs text-ash"> {t('directory.openStatus.nextDay')}</span>}
                        {note && <span className="block text-xs text-ash font-normal">{note}</span>}
                      </span>
                    );
                  })
                )}
                {day.mayDiffer && <span className="text-ash" aria-hidden> *</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {anyMayDiffer && <p className="text-xs text-ash">* {t('directory.openStatus.mayDiffer')}</p>}
    </div>
  );
}
